/**
 * useIntegrationConnect — encapsulates all "connect this integration" flows
 * so the consumer (IntegrationsCard) only needs a single `connect(platform)`
 * call per click. Keeps the existing OAuth/redirect/dialog logic intact —
 * the only thing that changes is *who* triggers it (formerly the picker
 * modal, now the integrations grid directly).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  fetchIntegrationAuthUrl,
  fetchMetaAuthUrl,
  fetchLinkedInAuthUrl,
  fetchTikTokAuthUrl,
  fetchMailchimpAuthUrl,
  fetchKlaviyoAuthUrl,
  fetchShopifyAuthUrl,
  fetchHubSpotAuthUrl,
  fetchMicrosoftAdsAuthUrl,
  fetchPinterestAdsAuthUrl,
  fetchSnapchatAdsAuthUrl,
  fetchRedditAdsAuthUrl,
  fetchAmazonAdsAuthUrl,
} from "../lib/oauth-popup";
import {
  ADAPTER_META,
  getScopesForPlatforms,
  GOOGLE_OAUTH_PLATFORMS as GOOGLE_OAUTH_PLATFORMS_ARRAY,
  META_OAUTH_PLATFORMS as META_OAUTH_PLATFORMS_ARRAY,
  LINKEDIN_OAUTH_PLATFORMS as LINKEDIN_OAUTH_PLATFORMS_ARRAY,
  TIKTOK_PLATFORM_VALUES as TIKTOK_OAUTH_PLATFORMS_ARRAY,
  MAILCHIMP_PLATFORM_VALUES as MAILCHIMP_OAUTH_PLATFORMS_ARRAY,
  KLAVIYO_PLATFORM_VALUES as KLAVIYO_OAUTH_PLATFORMS_ARRAY,
  SHOPIFY_OAUTH_PLATFORM_VALUES as SHOPIFY_OAUTH_PLATFORMS_ARRAY,
  HUBSPOT_PLATFORM_VALUES as HUBSPOT_OAUTH_PLATFORMS_ARRAY,
  MICROSOFT_ADS_PLATFORM_VALUES as MICROSOFT_ADS_PLATFORMS_ARRAY,
  PINTEREST_ADS_PLATFORM_VALUES as PINTEREST_ADS_PLATFORMS_ARRAY,
  SNAPCHAT_ADS_PLATFORM_VALUES as SNAPCHAT_ADS_PLATFORMS_ARRAY,
  REDDIT_ADS_PLATFORM_VALUES as REDDIT_ADS_PLATFORMS_ARRAY,
  AMAZON_ADS_PLATFORM_VALUES as AMAZON_ADS_PLATFORMS_ARRAY,
} from "@provenleap/integrations";
import type { ClientIntegration } from "../components/clients/types";
import type { ToastType } from "./useToast";
import { Button } from "@provenleap/ui";
import { PageSpeedUrlInput } from "../components/integrations/PageSpeedUrlInput";
import { CatalogPickerDialog } from "../components/integrations/CatalogPickerDialog";
import { fetchGrantCatalog, hasUsableGrant } from "./useGrantCatalog";

const GOOGLE_OAUTH_PLATFORMS = new Set<string>(GOOGLE_OAUTH_PLATFORMS_ARRAY);
const META_OAUTH_PLATFORMS = new Set<string>(META_OAUTH_PLATFORMS_ARRAY);
const LINKEDIN_OAUTH_PLATFORMS = new Set<string>(LINKEDIN_OAUTH_PLATFORMS_ARRAY);
const TIKTOK_OAUTH_PLATFORMS = new Set<string>(TIKTOK_OAUTH_PLATFORMS_ARRAY);
const MAILCHIMP_OAUTH_PLATFORMS = new Set<string>(MAILCHIMP_OAUTH_PLATFORMS_ARRAY);
const KLAVIYO_OAUTH_PLATFORMS = new Set<string>(KLAVIYO_OAUTH_PLATFORMS_ARRAY);
const SHOPIFY_OAUTH_PLATFORMS = new Set<string>(SHOPIFY_OAUTH_PLATFORMS_ARRAY);
const HUBSPOT_OAUTH_PLATFORMS = new Set<string>(HUBSPOT_OAUTH_PLATFORMS_ARRAY);
const MICROSOFT_ADS_PLATFORMS = new Set<string>(MICROSOFT_ADS_PLATFORMS_ARRAY);
const PINTEREST_ADS_PLATFORMS = new Set<string>(PINTEREST_ADS_PLATFORMS_ARRAY);
const SNAPCHAT_ADS_PLATFORMS = new Set<string>(SNAPCHAT_ADS_PLATFORMS_ARRAY);
const REDDIT_ADS_PLATFORMS = new Set<string>(REDDIT_ADS_PLATFORMS_ARRAY);
const AMAZON_ADS_PLATFORMS = new Set<string>(AMAZON_ADS_PLATFORMS_ARRAY);

/**
 * Story 32.2 — OAuth platforms whose `connect()` dispatch supports the
 * `forceNewGrant` "Add another grant" flow end-to-end (auth-url fetch helper
 * carries the flag + the callback's fresh-mint nulling exists). Derived from
 * the SAME package-exported family arrays the dispatch below uses (never a
 * hardcoded platform list, per the oauth-integration directive). The "Add
 * another grant" affordance derives its visibility from this set.
 *
 * Story 32-5 note: the five ads families (microsoft/pinterest/snapchat/
 * reddit/amazon) ARE now `connect()`-dispatchable (see
 * `isConnectDispatchable` below — the banner's Reconnect CTA), but stay OUT
 * of this set: their fetch helpers and callbacks have no forceNewGrant
 * support yet, so offering "Add another grant" there would silently reuse
 * the existing grant — the exact clobber 32-2 exists to prevent. When a
 * family gains forceNewGrant plumbing, add its array here and the affordance
 * appears automatically.
 */
export const ONE_CLICK_OAUTH_PLATFORMS: ReadonlySet<string> = new Set<string>([
  ...GOOGLE_OAUTH_PLATFORMS_ARRAY,
  ...META_OAUTH_PLATFORMS_ARRAY,
  ...LINKEDIN_OAUTH_PLATFORMS_ARRAY,
  ...TIKTOK_OAUTH_PLATFORMS_ARRAY,
  ...MAILCHIMP_OAUTH_PLATFORMS_ARRAY,
  ...KLAVIYO_OAUTH_PLATFORMS_ARRAY,
  ...SHOPIFY_OAUTH_PLATFORMS_ARRAY,
  ...HUBSPOT_OAUTH_PLATFORMS_ARRAY,
]);

/**
 * Story 32-5 AC #12 — every platform `connect()` can dispatch SOMETHING for
 * (an OAuth redirect, or the Shopify/HubSpot prompt flows). Derived from the
 * same package value-set unions as the dispatch — NO hardcoded platform
 * string list (oauth-integration directive). API-key families
 * (ahrefs/semrush/moz/callrail/calltrackingmetrics/trustpilot/yelp,
 * google-pagespeed) are NOT here: the TokenAuthFailedBanner shows an "Open
 * Integrations" fallback for those instead of a dead Reconnect button.
 */
const CONNECT_DISPATCHABLE_PLATFORMS: ReadonlySet<string> = new Set<string>([
  ...ONE_CLICK_OAUTH_PLATFORMS,
  ...MICROSOFT_ADS_PLATFORMS_ARRAY,
  ...PINTEREST_ADS_PLATFORMS_ARRAY,
  ...SNAPCHAT_ADS_PLATFORMS_ARRAY,
  ...REDDIT_ADS_PLATFORMS_ARRAY,
  ...AMAZON_ADS_PLATFORMS_ARRAY,
]);

/** Pure predicate for CTA-mode selection (Story 32-5 AC #11/#12). */
export function isConnectDispatchable(platform: string): boolean {
  return CONNECT_DISPATCHABLE_PLATFORMS.has(platform);
}

/**
 * Story 32.2 (review F1) — platforms whose `connect()` call hands off to a
 * SECONDARY prompt dialog instead of fetching an auth-url immediately
 * (Shopify's subdomain dialog: the store URL IS the flow). Consumers that
 * launched `connect()` from their own confirm dialog must close it at this
 * handoff — `connectingPlatform` is never set on this path, so the pending
 * close-block will not do it for them. Derived from the same package family
 * array the dispatch uses (never a hardcoded platform list).
 */
export const SECONDARY_PROMPT_OAUTH_PLATFORMS: ReadonlySet<string> =
  new Set<string>(SHOPIFY_OAUTH_PLATFORMS_ARRAY);

/**
 * Story 32.2 — the connections-page/tab URL the user should land back on
 * after the full-page OAuth round-trip (the 32-1 `?tab=` deep link).
 */
function connectionsReturnUrl(clientId: string): string {
  return `/clients/${clientId}?tab=integrations`;
}

/** Story 32.2 — options accepted by `connect()` (backward compatible). */
export interface ConnectOptions {
  /** Launch a fresh OAuth flow that never reuses/overwrites an existing grant. */
  forceNewGrant?: boolean;
}

interface UseIntegrationConnectArgs {
  clientId: string;
  connectedIntegrations: ClientIntegration[];
  showToast: (type: ToastType, message: string) => void;
  onIntegrationChange?: () => void;
}

interface UseIntegrationConnectResult {
  /** Currently-connecting platform (for spinner state). null when idle. */
  connectingPlatform: string | null;
  /** Trigger a connect flow for the given platform. */
  connect: (platform: string, opts?: ConnectOptions) => void;
  /**
   * Story 32-3: open the CatalogPickerDialog for a platform directly — the
   * post-OAuth `catalogCount > 1` consumers (redirect-flow search params,
   * popup-flow result) use this without re-running the pre-flight.
   */
  openCatalogPicker: (platform: string) => void;
  /** Mount this anywhere — renders all auxiliary dialogs (PageSpeed URL, Shopify subdomain, HubSpot reuse, catalog picker). */
  dialogs: React.ReactNode;
}

export function useIntegrationConnect({
  clientId,
  connectedIntegrations,
  showToast,
  onIntegrationChange,
}: UseIntegrationConnectArgs): UseIntegrationConnectResult {
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [pageSpeedDialogOpen, setPageSpeedDialogOpen] = useState(false);
  const [shopifyDialogOpen, setShopifyDialogOpen] = useState(false);
  /** Story 32.2: the Shopify subdomain dialog still shows under forceNewGrant
   *  (a different store = a different subdomain — the dialog IS the flow);
   *  this remembers the flag for the dialog's eventual fetch. */
  const [shopifyForceNewGrant, setShopifyForceNewGrant] = useState(false);
  /** When set, ask the user whether to reuse the existing HubSpot connection. */
  const [hubspotReusePrompt, setHubspotReusePrompt] = useState(false);
  /** Story 32-3: platform whose CatalogPickerDialog is open (null = closed). */
  const [catalogPickerPlatform, setCatalogPickerPlatform] = useState<string | null>(null);
  /** §4.6 lastValue — lingers ~300ms after close so the exit animation plays. */
  const [lastCatalogPlatform, setLastCatalogPlatform] = useState<string | null>(null);
  useEffect(() => {
    if (catalogPickerPlatform) {
      setLastCatalogPlatform(catalogPickerPlatform);
      return;
    }
    if (!lastCatalogPlatform) return;
    const timer = setTimeout(() => setLastCatalogPlatform(null), 300);
    return () => clearTimeout(timer);
  }, [catalogPickerPlatform, lastCatalogPlatform]);

  const hasHubSpotConnection = useMemo(
    () => connectedIntegrations.some((i) => HUBSPOT_OAUTH_PLATFORMS.has(i.type)),
    [connectedIntegrations],
  );

  /** Common error/finalize wrapper for fetch+redirect flows. */
  const runRedirect = useCallback(
    (platform: string, fetchUrl: () => Promise<string>, errorMsg: string) => {
      setConnectingPlatform(platform);
      fetchUrl()
        .then((url) => {
          window.location.href = url;
        })
        .catch((err: unknown) => {
          setConnectingPlatform(null);
          // Surface the backend's detail message when available so the user
          // sees the real cause (e.g. "Google OAuth not configured").
          const detail = err instanceof Error ? err.message : "";
          showToast("error", detail || errorMsg);
          // eslint-disable-next-line no-console
          console.error("[connect]", platform, err);
        });
    },
    [showToast],
  );

  /**
   * Story 32.2: per-flow fetch args. Default path passes NOTHING extra
   * (byte-identical request bodies); the forced flow carries the flag plus a
   * returnUrl pointing back at the connections tab.
   */
  const fetchArgs = useCallback(
    (opts?: ConnectOptions): { returnUrl?: string; fetchOpts?: { forceNewGrant: true } } =>
      opts?.forceNewGrant
        ? { returnUrl: connectionsReturnUrl(clientId), fetchOpts: { forceNewGrant: true } }
        : {},
    [clientId],
  );

  const startGoogle = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const scopes = getScopesForPlatforms([platform]);
      if (scopes.length === 0) return;
      const { returnUrl, fetchOpts } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchIntegrationAuthUrl(clientId, scopes, returnUrl, fetchOpts),
        "Failed to initiate connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startMeta = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl, fetchOpts } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchMetaAuthUrl(clientId, returnUrl, fetchOpts),
        "Failed to initiate Meta connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startLinkedIn = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl, fetchOpts } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchLinkedInAuthUrl(clientId, returnUrl, fetchOpts),
        "Failed to initiate LinkedIn connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startTikTok = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl, fetchOpts } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchTikTokAuthUrl(clientId, returnUrl, fetchOpts),
        "Failed to initiate TikTok connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startMailchimp = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl, fetchOpts } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchMailchimpAuthUrl(clientId, returnUrl, fetchOpts),
        "Failed to initiate Mailchimp connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startKlaviyo = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl, fetchOpts } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchKlaviyoAuthUrl(clientId, returnUrl, fetchOpts),
        "Failed to initiate Klaviyo connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startHubSpot = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl, fetchOpts } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchHubSpotAuthUrl(clientId, returnUrl, fetchOpts),
        "Failed to initiate HubSpot connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  /**
   * Story 32-5 AC #12 — the five redirect-flow ads families, mechanical
   * clones of the Klaviyo branch. Their fetch helpers take (clientId,
   * returnUrl) only — no forceNewGrant plumbing exists for these families
   * yet (see the ONE_CLICK_OAUTH_PLATFORMS docstring), so only the returnUrl
   * half of fetchArgs is threaded.
   */
  const startMicrosoftAds = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchMicrosoftAdsAuthUrl(clientId, returnUrl),
        "Failed to initiate Microsoft Advertising connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startPinterestAds = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchPinterestAdsAuthUrl(clientId, returnUrl),
        "Failed to initiate Pinterest Advertising connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startSnapchatAds = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchSnapchatAdsAuthUrl(clientId, returnUrl),
        "Failed to initiate Snapchat Advertising connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startRedditAds = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchRedditAdsAuthUrl(clientId, returnUrl),
        "Failed to initiate Reddit Advertising connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  const startAmazonAds = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      const { returnUrl } = fetchArgs(opts);
      runRedirect(
        platform,
        () => fetchAmazonAdsAuthUrl(clientId, returnUrl),
        "Failed to initiate Amazon Ads connection",
      );
    },
    [clientId, runRedirect, fetchArgs],
  );

  /**
   * Story 32-3: the per-family OAuth dispatch, extracted from `connect()` so
   * the picker's AC #12 escape hatch ("Connect a different account") can
   * start the EXACT start* path connect() would have used — without
   * re-running the pre-flight.
   */
  const dispatchOAuth = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      if (GOOGLE_OAUTH_PLATFORMS.has(platform)) return startGoogle(platform, opts);
      if (META_OAUTH_PLATFORMS.has(platform)) return startMeta(platform, opts);
      if (LINKEDIN_OAUTH_PLATFORMS.has(platform)) return startLinkedIn(platform, opts);
      if (TIKTOK_OAUTH_PLATFORMS.has(platform)) return startTikTok(platform, opts);
      if (MAILCHIMP_OAUTH_PLATFORMS.has(platform)) return startMailchimp(platform, opts);
      if (KLAVIYO_OAUTH_PLATFORMS.has(platform)) return startKlaviyo(platform, opts);
      // Story 32-5 AC #12: the five redirect-flow ads families.
      if (MICROSOFT_ADS_PLATFORMS.has(platform)) return startMicrosoftAds(platform, opts);
      if (PINTEREST_ADS_PLATFORMS.has(platform)) return startPinterestAds(platform, opts);
      if (SNAPCHAT_ADS_PLATFORMS.has(platform)) return startSnapchatAds(platform, opts);
      if (REDDIT_ADS_PLATFORMS.has(platform)) return startRedditAds(platform, opts);
      if (AMAZON_ADS_PLATFORMS.has(platform)) return startAmazonAds(platform, opts);
      // Unknown platform — silently noop. Shouldn't happen for whitelisted tiles.
    },
    [
      startGoogle,
      startMeta,
      startLinkedIn,
      startTikTok,
      startMailchimp,
      startKlaviyo,
      startMicrosoftAds,
      startPinterestAds,
      startSnapchatAds,
      startRedditAds,
      startAmazonAds,
    ],
  );

  /**
   * Story 32-3 AC #1 — multi-tenant pre-flight: before any OAuth dispatch,
   * fetch the platform's grant catalog; ≥1 healthy/expiring_soon grant →
   * open the CatalogPickerDialog INSTEAD of OAuth. Empty/no usable grants OR
   * any pre-flight failure → fall through to OAuth (NEVER block a connect on
   * the pre-flight — non-fatal by contract).
   */
  const runCatalogPreflight = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      setConnectingPlatform(platform);
      fetchGrantCatalog(platform)
        .then((catalog) => {
          setConnectingPlatform(null);
          if (hasUsableGrant(catalog)) {
            setCatalogPickerPlatform(platform);
          } else {
            dispatchOAuth(platform, opts);
          }
        })
        .catch((err: unknown) => {
          setConnectingPlatform(null);
          // eslint-disable-next-line no-console
          console.error("[connect] catalog pre-flight failed", platform, err);
          dispatchOAuth(platform, opts);
        });
    },
    [dispatchOAuth],
  );

  const connect = useCallback(
    (platform: string, opts?: ConnectOptions) => {
      // PageSpeed → API-key flow, opens the URL prompt
      if (platform === "google-pagespeed") {
        setPageSpeedDialogOpen(true);
        return;
      }
      // Shopify → subdomain prompt (still shows under forceNewGrant — a
      // different store IS a different subdomain, the dialog is the flow)
      if (SHOPIFY_OAUTH_PLATFORMS.has(platform)) {
        setShopifyForceNewGrant(opts?.forceNewGrant === true);
        setShopifyDialogOpen(true);
        return;
      }
      // HubSpot — if already connected to this client tenant, ask first.
      // Story 32.2: the reuse prompt is SKIPPED under forceNewGrant — the
      // "Add another grant" affordance already IS the "connect new" intent.
      // (PageSpeed/Shopify/HubSpot are all single-tenant in the registry, so
      // evaluating them BEFORE the 32-3 pre-flight is provably unchanged.)
      if (HUBSPOT_OAUTH_PLATFORMS.has(platform)) {
        if (hasHubSpotConnection && !opts?.forceNewGrant) {
          setHubspotReusePrompt(true);
          return;
        }
        startHubSpot(platform, opts);
        return;
      }
      // Story 32-3 AC #1: multi-tenant pre-flight — derived ONLY from the
      // registry's oauthGrantScope (V1-effective values; never a hardcoded
      // platform list). forceNewGrant skips it by definition: "Add another
      // grant" IS the fresh-OAuth intent (32-2).
      if (
        !opts?.forceNewGrant &&
        ADAPTER_META[platform as keyof typeof ADAPTER_META]?.oauthGrantScope ===
          "multi-tenant"
      ) {
        runCatalogPreflight(platform, opts);
        return;
      }
      dispatchOAuth(platform, opts);
    },
    [hasHubSpotConnection, startHubSpot, runCatalogPreflight, dispatchOAuth],
  );

  /** Story 32-3: direct picker open for the post-OAuth catalogCount consumers. */
  const openCatalogPicker = useCallback((platform: string) => {
    setCatalogPickerPlatform(platform);
  }, []);

  const dialogs = (
    <>
      <PageSpeedUrlInput
        open={pageSpeedDialogOpen}
        onOpenChange={setPageSpeedDialogOpen}
        clientId={clientId}
        showToast={showToast}
        onIntegrationCreated={onIntegrationChange}
      />
      <ShopifyStoreUrlDialog
        open={shopifyDialogOpen}
        onOpenChange={setShopifyDialogOpen}
        clientId={clientId}
        showToast={showToast}
        forceNewGrant={shopifyForceNewGrant}
      />
      <HubSpotReuseDialog
        open={hubspotReusePrompt}
        onOpenChange={setHubspotReusePrompt}
        onConfirmNew={() => {
          setHubspotReusePrompt(false);
          startHubSpot("hubspot");
        }}
      />
      {/* Story 32-3: §4.6 lastValue mounting — non-null platform required */}
      {lastCatalogPlatform && (
        <CatalogPickerDialog
          open={!!catalogPickerPlatform}
          onOpenChange={(next) => {
            if (!next) setCatalogPickerPlatform(null);
          }}
          platform={lastCatalogPlatform}
          defaultClientId={clientId}
          showToast={showToast}
          onAttached={onIntegrationChange}
          onConnectNew={() => dispatchOAuth(lastCatalogPlatform)}
        />
      )}
    </>
  );

  return { connectingPlatform, connect, openCatalogPicker, dialogs };
}

// ============================================================================
// ShopifyStoreUrlDialog (extracted from IntegrationPickerModal)
// ============================================================================

function ShopifyStoreUrlDialog({
  open,
  onOpenChange,
  clientId,
  showToast,
  forceNewGrant = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  showToast: (type: ToastType, message: string) => void;
  /** Story 32.2 — forwards the "Add another grant" flag to the auth-url fetch. */
  forceNewGrant?: boolean;
}) {
  const [subdomain, setSubdomain] = useState("");
  const [error, setError] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = useCallback(async () => {
    let trimmed = subdomain.trim().toLowerCase();
    if (!trimmed) {
      setError("Please enter your Shopify subdomain");
      return;
    }
    trimmed = trimmed
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "")
      .replace(/\.myshopify\.com$/, "");
    if (!/^[a-zA-Z0-9][a-zA-Z0-9-]*$/.test(trimmed)) {
      setError("Invalid subdomain. Use only letters, numbers, and hyphens.");
      return;
    }
    setError("");
    setIsConnecting(true);
    try {
      const url = forceNewGrant
        ? await fetchShopifyAuthUrl(
            clientId,
            trimmed,
            connectionsReturnUrl(clientId),
            { forceNewGrant: true },
          )
        : await fetchShopifyAuthUrl(clientId, trimmed);
      window.location.href = url;
    } catch (err) {
      setIsConnecting(false);
      const message =
        err instanceof Error ? err.message : "Failed to initiate Shopify connection";
      showToast("error", message);
    }
  }, [subdomain, clientId, showToast, forceNewGrant]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 bg-black/35 z-50" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-[calc(100vw-2rem)] sm:max-w-[440px] rounded-3xl bg-background p-8 shadow-lg"
          style={{ border: "0.5px solid hsl(var(--input))" }}
        >
          <DialogPrimitive.Title className="text-lg font-semibold mb-2">
            Connect Shopify Store
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="text-sm text-muted-foreground mb-6">
            Enter your Shopify store subdomain to connect.
          </DialogPrimitive.Description>

          <div className="space-y-4">
            <div>
              <div className="flex items-center">
                <input
                  type="text"
                  value={subdomain}
                  onChange={(e) => {
                    setSubdomain(e.target.value);
                    setError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleConnect();
                  }}
                  placeholder="mystore"
                  className={`flex-1 rounded-l-2xl px-4 py-2.5 text-[13px] shadow-xs text-foreground placeholder:text-foreground/50 outline-none ${
                    error ? "border-destructive" : ""
                  }`}
                  style={{
                    border: `0.5px solid ${
                      error ? "hsl(var(--destructive))" : "var(--hairline)"
                    }`,
                    borderRight: "none",
                  }}
                  disabled={isConnecting}
                />
                <span
                  className="rounded-r-2xl px-3 py-2.5 text-[13px] text-muted-foreground bg-muted shadow-xs select-none"
                  style={{ border: "0.5px solid var(--hairline)", borderLeft: "none" }}
                >
                  .myshopify.com
                </span>
              </div>
              {error && <p className="text-xs text-destructive mt-1.5">{error}</p>}
              <p className="text-xs text-muted-foreground mt-1.5">
                Your Shopify subdomain — find it in Shopify Admin &gt; Settings &gt; Domains
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                style={{ border: "0.5px solid hsl(var(--input))" }}
                disabled={isConnecting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="brand"
                className="rounded-xl cursor-pointer"
                onClick={handleConnect}
                disabled={isConnecting || !subdomain.trim()}
              >
                {isConnecting ? "Connecting..." : "Connect"}
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ============================================================================
// HubSpotReuseDialog — asks whether to use existing HubSpot connection
// ============================================================================

function HubSpotReuseDialog({
  open,
  onOpenChange,
  onConfirmNew,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmNew: () => void;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 bg-black/35 z-50" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-[calc(100vw-2rem)] sm:max-w-[440px] rounded-3xl bg-background p-8 shadow-lg"
          style={{ border: "0.5px solid hsl(var(--input))" }}
        >
          <DialogPrimitive.Title className="text-lg font-semibold mb-2">
            HubSpot already connected
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="text-sm text-muted-foreground mb-6">
            You already have a HubSpot connection on this client. Connecting again
            will create a new authorization (and the previous one stays linked).
            Continue?
          </DialogPrimitive.Description>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
              style={{ border: "0.5px solid hsl(var(--input))" }}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="brand"
              className="rounded-xl cursor-pointer"
              onClick={onConfirmNew}
            >
              Connect new
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
